import { createContext, useContext, type ReactNode } from 'react';

/**
 * Context flags that change how block components render inside the marketing
 * admin editor preview versus the public published site.
 *
 * - {@link MarketingPreviewValue.adminPreview}: when `true`, blocks that load
 *   authed data (e.g. {@link ReplayBlock}) hit their authenticated REST
 *   endpoints instead of the `/public/*` mirrors that only serve published
 *   content. This removes the "must publish first" footgun for draft pages.
 * - {@link MarketingPreviewValue.selectedBlockId}: id of the currently
 *   selected block in the admin canvas. Used by `PageRenderer` to render the
 *   selected ring; consumers in deeply nested block components can also use
 *   this to short-circuit interactive UI when the block isn't in focus.
 * - {@link MarketingPreviewValue.onPatchField}: optional callback used by
 *   inline-edit primitives (`EditableText`, `EditableMarkdown`) to report a
 *   committed text edit to the editor. The path uses dot notation
 *   (`'title'`, `'items.2.description'`) and is applied with `setByPath`.
 */
export interface MarketingPreviewValue {
  adminPreview: boolean;
  selectedBlockId?: string | null;
  onPatchField?: (blockId: string, path: string, value: unknown) => void;
}

const DEFAULT: MarketingPreviewValue = { adminPreview: false };

const MarketingPreviewContext = createContext<MarketingPreviewValue>(DEFAULT);

export interface MarketingPreviewProviderProps {
  adminPreview: boolean;
  selectedBlockId?: string | null;
  onPatchField?: (blockId: string, path: string, value: unknown) => void;
  children: ReactNode;
}

export function MarketingPreviewProvider({
  adminPreview,
  selectedBlockId = null,
  onPatchField,
  children,
}: MarketingPreviewProviderProps) {
  return (
    <MarketingPreviewContext.Provider value={{ adminPreview, selectedBlockId, onPatchField }}>
      {children}
    </MarketingPreviewContext.Provider>
  );
}

export function useMarketingPreview(): MarketingPreviewValue {
  return useContext(MarketingPreviewContext);
}
